import React, { useState } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ChevronRight, Award, Zap, Palette, Phone, Lock, CheckCircle, X } from 'lucide-react';
import { CareerPath, Achievement } from '@/types/career';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { useCareerPathDetails } from '@/context/CareerPathContext';  // For detailed path info

const careerPaths: CareerPath[] = [
  {
    id: 'frontend',
    title: 'Frontend Fundamentals',
    description: 'Master the basics of web development',
    icon: <Zap className="w-5 h-5" />,
    achievements: [
      {
        name: 'HTML Master',
        description: 'Create well-structured web pages using semantic HTML',
        isCompleted: true,
        completedDate: '2024-03-15'
      },
      {
        name: 'CSS Guru',
        description: 'Style websites with modern CSS techniques',
        isCompleted: false
      },
      {
        name: 'JavaScript Expert',
        description: 'Build interactive web features with JavaScript',
        isCompleted: false
      }
    ],
    isUnlocked: true,
    progress: 75,
    details: 'Start your journey into web development by mastering the core technologies that power the web.',
    requirements: [
      'Basic computer skills',
      'Text editor (like VS Code)',
      'Modern web browser'
    ]
  }
  // Additional paths can be added here
];

export default function CareerPathVisualizer() {
  const [selectedPath, setSelectedPath] = useState<CareerPath | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [completedAchievement, setCompletedAchievement] = useState<string>('');

  const handlePathClick = (path: CareerPath) => {
    if (path.isUnlocked) {
      setSelectedPath(path);
    }
  };

  const handleAchievementComplete = async (pathId: string, achievementName: string) => {
    try {
      setCompletedAchievement(achievementName);
      setShowConfetti(true);
      // Add achievement completion logic here
      setTimeout(() => {
        setShowConfetti(false);
        setCompletedAchievement('');
      }, 3000);
    } catch (error) {
      console.error('Failed to complete achievement:', error);
    }
  };

  return (
    <div className="space-y-6 relative">
      <h2 className="text-2xl font-bold">Your Career Journey</h2>
      
      {/* Achievement Animation */}
      {showConfetti && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 transform animate-bounce text-center">
            <div className="text-3xl mb-4">🎉</div>
            <h3 className="text-xl font-bold text-green-600">Achievement Unlocked!</h3>
            <p className="mt-2">{completedAchievement}</p>
          </div>
        </div>
      )}

      {/* Path Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {careerPaths.map((path) => (
          <Card 
            key={path.id} 
            className={`transform transition-all duration-300 hover:scale-102 cursor-pointer ${
              path.isUnlocked 
                ? 'hover:shadow-lg hover:border-blue-300' 
                : 'bg-gray-50'
            }`}
            onClick={() => handlePathClick(path)}
          >
            <CardContent className={path.isUnlocked ? '' : 'opacity-75'}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-primary-100 rounded-lg">
                    {path.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold">{path.title}</h3>
                    <p className="text-sm text-gray-600">{path.description}</p>
                  </div>
                </div>
                {!path.isUnlocked && <Lock className="w-5 h-5 text-gray-400" />}
              </div>
              <Progress value={path.progress} className="mb-4" />
              <div className="flex justify-between text-sm text-gray-600">
                <span>{path.progress}% Complete</span>
                <span>{path.achievements.length} Achievements</span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Detailed View Modal */}
      <Dialog open={selectedPath !== null} onOpenChange={() => setSelectedPath(null)}>
        <DialogContent className="max-w-2xl">
          {selectedPath && (
            <>
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    {selectedPath.icon}
                  </div>
                  <h3 className="text-2xl font-bold">{selectedPath.title}</h3>
                </div>
              </div>
              
              <p className="text-gray-600 mb-6">{selectedPath.details}</p>
              
              <div className="space-y-6">
                <div>
                  <h4 className="font-semibold mb-2">Requirements</h4>
                  <ul className="list-disc list-inside space-y-1 text-gray-600">
                    {selectedPath.requirements.map((req, index) => (
                      <li key={index}>{req}</li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Achievements</h4>
                  <div className="space-y-4">
                    {selectedPath.achievements.map((achievement) => (
                      <div 
                        key={achievement.name}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                      >
                        <div>
                          <h5 className="font-medium">{achievement.name}</h5>
                          <p className="text-sm text-gray-600">{achievement.description}</p>
                        </div>
                        {achievement.isCompleted ? (
                          <div className="flex items-center text-green-600">
                            <CheckCircle className="w-5 h-5 mr-2" />
                            <span className="text-sm">
                              {achievement.completedDate && `Completed ${achievement.completedDate}`}
                            </span>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleAchievementComplete(selectedPath.id, achievement.name)}
                            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                          >
                            Complete
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
} 